import {
  ACLMap,
  BackendErrorCodesEnum,
  BasicPrivilegeTypeEnum,
  DirentPermissionMap,
  OrgbrainFatal,
  OrgbrainWarn,
  PrivilegeType,
} from '@orgbrain/lib-data';
import { PermissionEnum, permissionToOrgGroup, Role, rolesToOrgGroupArray } from '@orgbrain/roles';
import { DirentNode, DirentUser, PrivilegeError, SharedFolderAssociationRule } from './classes';
import { ControlTables } from './file-privileges';
import { CurrentUser, Organization } from './lib-admin';
import { getOrganizationRoleMapFromUser } from './rescued-from-roles';
export const FILENAME_EXISTS_ERROR_MESSAGE = 'Filename exists';
export function uniqueName({
  name,
  list,
  onCollisionThrow,
}: {
  name: string;
  list: DirentNode[];
  onCollisionThrow?: boolean;
}) {
  const hasExt = name.indexOf('.') > 0;

  if (!list?.length) {
    return name;
  }

  if (hasExt) {
    const toks = name.split('.');
    const ext = toks.length > 0 ? '.' + toks[toks.length - 1] : '';

    let baseName;

    if (ext.length > 0) {
      const ii = name.lastIndexOf(ext);
      baseName = name.substr(0, ii);
    } else {
      baseName = name;
    }

    let newName = name;

    // eslint-disable-next-line no-constant-condition
    for (let count = 0; true; count++) {
      if (count === 0) {
        newName = baseName + ext;
      } else {
        newName = baseName + '(' + count + ')' + ext;
      }
      const found = list.find((item) => item.name === newName);

      if (!found) {
        break;
      } else if (onCollisionThrow) {
        throw new Error(`${FILENAME_EXISTS_ERROR_MESSAGE}:::${found._id}`);
      }
    }

    return newName;
  } else {
    let newName = name;

    // eslint-disable-next-line no-constant-condition
    for (let count = 0; true; count++) {
      if (count > 0) {
        newName = `${name}-${count}`;
      }
      const found = list.find((item) => item.name === newName);

      if (!found) {
        break;
      } else if (onCollisionThrow) {
        throw new Error(`${FILENAME_EXISTS_ERROR_MESSAGE}:::${found._id}`);
      }
    }

    return newName;
  }
}

export function uniqueAndCleanDirentName({
  name,
  oldName,
  list,
  onCollisionThrow,
}: {
  name: string;
  oldName?: string;
  list: DirentNode[];
  onCollisionThrow?: boolean;
}) {
  if (name === oldName) {
    return name;
  }
  name = cleanName(name);
  const result = uniqueName({ name, list, onCollisionThrow });

  return result;
}

export function cleanName(name: string) {
  return (
    (name || '')
      // eslint-disable-next-line no-useless-escape
      .replace(/[/\\?\+%*#&:|"<>\(\)]/g, '_')
      .replace(/_+/g, '_')
      .trim()
  );
}

export function getDirentUserVinilla({
  organizationId,
  sharedFolderOrganizationIds,
  dbUser,
  sharedAssociationRules,
}: {
  organizationId: string;
  sharedFolderOrganizationIds?: string[];
  dbUser: CurrentUser;
  sharedAssociationRules?: SharedFolderAssociationRule[];
}): DirentUser {
  organizationId = organizationId && organizationId.toString();
  if (organizationId.length !== 24) {
    throw new OrgbrainFatal(`Expceted organizationId to be 24 chars got ${organizationId}`);
  }

  const org = dbUser.organizations.find((x) => x._id.toString() === organizationId);
  if (!org) {
    throw new OrgbrainWarn(`User does non belong to the organization ${organizationId}`);
  }

  const roles: Array<Role> = org.roles;

  const userId = dbUser._id.toString();

  const myOrg = (sharedAssociationRules || []).find(
    (rule) => rule.organization.toString() === organizationId.toString()
  );

  const roleMap = getOrganizationRoleMapFromUser(organizationId, dbUser);
  const groups = rolesToOrgGroupArray({
    organizationId,
    roles,
    userId: userId?.toString(),
    sharedFolderOrganizationIds,
    hasWritePermission: myOrg && myOrg.hasWritePermission,
    roleMap,
  });

  const user: DirentUser = {
    userId: dbUser._id?.toString(),
    groups,
    super: dbUser.superuser,
  };

  return user;
}

// Test for permission.
// By default this throws a privilege error if user does not have permission.

export function hasDirentPrivilegeNEW({
  privilegeType,
  dirent,
  user,
  noThrow,
}: {
  privilegeType: BasicPrivilegeTypeEnum;
  dirent: DirentNode;
  user: DirentUser;
  noThrow?: boolean;
}): boolean {
  try {
    const map: DirentPermissionMap = dirent?.direntPermissionsMap;
    if (!map) {
      throw new OrgbrainFatal(
        'OrgbrainFatal: Map does not exist in hasDirentPrivilegeNEW, dirent: ' +
          JSON.stringify(dirent),
        BackendErrorCodesEnum.SystemError
      );
    }

    for (const group of user.groups) {
      if (privilegeType === 'READ' && map[group]) {
        return true;
      } else if (
        privilegeType === BasicPrivilegeTypeEnum.READWRITE &&
        map[group] === BasicPrivilegeTypeEnum.READWRITE
      ) {
        return true;
      }
    }
    throw new PrivilegeError('hasDirentPrivilegeNEW');
  } catch (err) {
    // console.error(err);
    if (!noThrow) throw err;
    return false;
  }
}

/**    THIS IS A HELPER DO NOT USE UNLESS YOU KNOW WHAT YOU ARE DOING -PJL
 *
 *  Check user has privilegeType  given an acl and ecl  and scl.
 *  If not throws an error (unless noThrow is set)
 *
 *  To succed privillege should exist in the ACL but not be in the ECL
 *
 *
 *  This will be deprecated in the future see hasDirentPrivilegeNEW()
 *
 *
 *
 */

export function hasPrivilege_DEPRECATED({
  privilegeType,
  acl,
  ecl,
  scl,
  user,
  noThrow,
}: {
  privilegeType: PrivilegeType;
  acl: ACLMap;
  ecl?: ACLMap;
  scl?: ACLMap;
  user: { groups: string[] };
  noThrow?: boolean;
}): boolean {
  // Look at ACL at this level
  if (!user) {
    throw new PrivilegeError('user undefined');
  }

  if (!user.groups) {
    throw new PrivilegeError('user.groups undefined');
  }
  if (acl) {
    for (const group of Object.keys(acl)) {
      const hasSCL = scl && scl[group] && scl[group][privilegeType];

      if (hasSCL) {
        return true;
      }

      const hasECL = ecl && ecl[group] && ecl[group][privilegeType];

      if (hasECL) continue; // ignore anything in the exclude list.

      if (user.groups.find((x) => x === group)) {
        const priv = acl[group][privilegeType];
        if (priv !== undefined) {
          if (priv) return true;
        }
      }
    }
  }
  if (noThrow) return false;
  throw new PrivilegeError(
    `missing privilege,acl:  ${JSON.stringify(acl)}, ecl:  ${JSON.stringify(
      ecl
    )},privilegeType:  ${privilegeType},user.groups:  ${user.groups}`
  );
}

export function combineACL(a: ACLMap, b: ACLMap): ACLMap {
  const x: ACLMap = {};

  if (a) {
    for (const group of Object.keys(a)) {
      x[group] = {};
      if (a[group].READ) x[group].READ = true;
      if (a[group].WRITE) x[group].WRITE = true;
    }
  }

  if (b) {
    for (const group of Object.keys(b)) {
      x[group] = x[group] || {};
      if (b[group].READ) x[group].READ = true;
      if (b[group].WRITE) x[group].WRITE = true;
    }
  }

  return x;
}

// Remove items form ACL that are in ECL
// Can be used for other things as well.  Sorry about the confusion.
// Maybe rename acl->orig  and  ecl->filter
export function filterACL({ acl, ecl }: { acl: ACLMap; ecl: ACLMap }): ACLMap {
  const ret: ACLMap = {};

  if (!acl) {
    return ret;
  }
  // Now filter
  for (const group of Object.keys(acl)) {
    try {
      for (const privilegeType of Object.keys(PrivilegeType)) {
        if (!acl[group][privilegeType]) continue;
        if (ecl && ecl[group] && ecl[group][privilegeType]) continue; // ignore anything in the exclude list.
        ret[group] = ret[group] || {};
        ret[group][privilegeType] = true;
      }
    } catch (error) {
      console.log('filterACL: error', error);
    }
  }

  return ret;
}

// Remove items form ACL that are in ECL
export function filterACLaddSCL({
  acl,
  ecl,
  scl,
}: {
  acl: ACLMap;
  ecl: ACLMap;
  scl: ACLMap;
}): ACLMap {
  let ret: ACLMap = filterACL({ acl, ecl });

  // Now filter
  if (scl) {
    for (const group of Object.keys(scl)) {
      for (const privilegeType of Object.keys(PrivilegeType)) {
        if (!scl[group][privilegeType]) continue;
        ret[group] = ret[group] || {};
        ret[group][privilegeType] = true;
      }
    }
  }
  return ret;
}
export function aclMapToArray({ acl }: { acl: ACLMap }): { group: string; privs: string[] }[] {
  const aclArray = Object.keys(acl).map((key) => {
    const priv = acl[key];
    let group = key.substr(25);
    if (group === '') group = 'organization';
    return { group, privs: Object.keys(priv) };
  });

  return aclArray;
}

/**
 *
 * Create group array  given organization and array of permissions
 *
 *
 */
export const controlTableToAccessControls = function ({
  organizationId,
  controlTables,
}: {
  organizationId: string;
  controlTables: ControlTables;
}): { acl: ACLMap; ecl: ACLMap } {
  let acl: ACLMap;
  let ecl: ACLMap;

  if (controlTables) {
    if (controlTables.acl) {
      for (const item of controlTables.acl) {
        const perm = Object.keys(item)[0];
        const grp = permissionToOrgGroup({ organizationId, perm: perm as PermissionEnum });
        acl = acl || {};
        acl[grp] = item[perm];
      }
    }
    if (controlTables.ecl) {
      for (const item of controlTables.ecl) {
        const perm = Object.keys(item)[0];
        const grp = permissionToOrgGroup({ organizationId, perm: perm as PermissionEnum });
        ecl = ecl || {};
        ecl[grp] = item[perm];
      }
    }
  }
  return { acl, ecl };
};

export function getFileExtension(fileName) {
  let extension = '';
  fileName = (fileName || '').trim();
  const tokens = fileName.split('.').map((i) => i.trim());
  extension = tokens[tokens.length - 1] || '';
  return extension;
}

export function guessExtension(fileType) {
  fileType = fileType || '';
  const toks = fileType.split('/');
  const ext = toks[toks.length - 1];
  return ext || '';
}

export function makeMimeType(extension) {
  let result = (extension || '').toLowerCase();
  if (extension) {
    if (['png', 'jpg', 'jpeg', 'gif'].includes(extension)) {
      result = `image/${extension}`;
    } else if (extension === 'svg') {
      result = `image/svg+xml`;
    } else if (extension === 'mp4') {
      result = 'video/mp4';
    } else if (extension === 'pdf') {
      result = 'application/pdf';
    } else if (extension === 'txt') {
      result = 'text/plain';
    }
  }

  return result;
}

export function getOrgNameForEmailFromCurrentUser({
  currentUser,
  org,
}: {
  currentUser: CurrentUser;
  org: Organization;
}) {
  let entityName = org.name;
  if (org.organizationGroupParentId) {
    const parentOrg = (currentUser.organizations || []).find(
      (item) => item._id === org.organizationGroupParentId
    );
    if (parentOrg) {
      entityName = Organization.subgroupName(parentOrg, org);
    }
  }

  return entityName;
}

export function roundToPrecision(number, precision) {
  // Validate inputs
  number = +number;
  if (typeof number !== 'number' || isNaN(number)) {
    number = 0;
  }

  if (typeof precision !== 'number' || precision < 0) {
    precision = 0;
  }

  // Calculate the factor to multiply by for the specified precision
  const factor = Math.pow(10, precision);

  // Round the number to the specified precision
  const roundedNumber = Math.round(number * factor) / factor;

  return roundedNumber;
}
